import React from "react";
import { toast } from "react-toastify";
import styled from "styled-components";

const ToastCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "DM Sans", "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  padding: 12px 16px;
  background: ${props => props.bgColor || "#ffffff"};
  border-radius: 12px;
  border: 1px solid ${props => props.borderColor || "#E5E7EB"};
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.06);
  min-width: 290px;
  max-width: 370px;
  margin-bottom: 8px;
  position: relative;
  overflow: hidden;
  border-left: 2px solid ${props => props.accentColor || "transparent"};

  /* Left accent accent line */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: ${props => props.accentColor || "#10B981"};
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${props => props.bgColor || "#ECFDF5"};
  color: ${props => props.accentColor || "#10B981"};
  flex-shrink: 0;
`;

const Message = styled.span`
  color: #1F2937;
  font-size: 13.5px;
  line-height: 1.4;
  font-weight: 500;
  flex: 1;
`;

// Clean Inline SVG Icons
const SuccessIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.6666 5L7.49992 14.1667L3.33325 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ErrorIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const WarningIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 13V11M10 7H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const alertStyle = (callbackFn) => ({
  position: "top-right",
  autoClose: 3500,
  hideProgressBar: true, // much cleaner without progress bar
  closeOnClick: true,
  pauseOnHover: true,
  draggable: false,
  className: "custom-toast-container",
  style: {
    background: "transparent",
    boxShadow: "none",
    padding: 0,
    minHeight: "auto",
  },
  onClose: () => (callbackFn ? callbackFn() : null),
});

// Inject global styles to completely override react-toastify's wrapping card wrapper
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.id = "custom-toastify-override-styles";
  style.innerHTML = `
    .Toastify__toast.custom-toast-container {
      background: transparent !important;
      box-shadow: none !important;
      padding: 0 !important;
      min-height: auto !important;
      border-radius: 0 !important;
    }
    .Toastify__toast.custom-toast-container .Toastify__toast-body {
      padding: 0 !important;
      margin: 0 !important;
    }
    .Toastify__toast.custom-toast-container .Toastify__close-button {
      position: absolute !important;
      top: 18px !important;
      right: 18px !important;
      z-index: 10 !important;
      color: #9CA3AF !important;
      opacity: 0.7 !important;
    }
    .Toastify__toast.custom-toast-container .Toastify__close-button:hover {
      opacity: 1 !important;
      color: #4B5563 !important;
    }
  `;
  document.head.appendChild(style);
}


export const SuccessToast = (message, callBackHandler = null) => {
  toast(
    <ToastCard borderColor="#A7F3D0" accentColor="#059669" bgColor="#ECFDF5">
      <IconWrapper accentColor="#059669" bgColor="#D1FAE5">
        <SuccessIcon />
      </IconWrapper>
      <Message>{message}</Message>
    </ToastCard>,
    alertStyle(callBackHandler)
  );
};

export const ErrorToast = (message, callBackHandler = null) => {
  toast(
    <ToastCard borderColor="#FCA5A5" accentColor="#DC2626" bgColor="#FEF2F2">
      <IconWrapper accentColor="#DC2626" bgColor="#FEE2E2">
        <ErrorIcon />
      </IconWrapper>
      <Message>{message}</Message>
    </ToastCard>,
    alertStyle(callBackHandler)
  );
};

export const WarningToast = (message, callBackHandler = null) => {
  toast(
    <ToastCard borderColor="#FCD34D" accentColor="#D97706" bgColor="#FFFBEB">
      <IconWrapper accentColor="#D97706" bgColor="#FEF3C7">
        <WarningIcon />
      </IconWrapper>
      <Message>{message}</Message>
    </ToastCard>,
    alertStyle(callBackHandler)
  );
};